import type { ThunkAction } from 'redux-thunk';
import type { AnyAction } from 'redux';

import { addPlaceHolderImageInResponse } from 'components/launchcalendar/LaunchCalendar.utility';
import { FAILED, FETCHING, SUCCESS } from 'constants/launchCalendar';
import { fetchErrorMiddleware, fetchErrorMiddlewareMaybeJson } from 'middleware/fetchErrorMiddleware';
import {
  ADD_UPCOMING_STYLES_QUICK_VIEW_DATA,
  SET_API_STATUS,
  SET_LAUNCH_CALENDAR_RECENT_LAUNCHES,
  SET_LAUNCH_CALENDAR_UPCOMING_STYLES
} from 'constants/reduxActions';
import type { LaunchCalendarRecentLaunches, LaunchCalendarUpcomingStyles, UpcomingStylesQuickViewData } from 'types/launchCalendar';
import type { AppState } from 'types/app';
import { selectMafiaConfig } from 'selectors/environment';
import { getLaunchCalendarRecentLaunches, getLaunchCalendarUpcomingStyles, subscribeToUpcomingStylesNotificationZSub } from 'apis/mafia';
import { trackError } from 'helpers/ErrorUtils';
import { err, setError } from 'actions/errors';
import { productBundle } from 'apis/cloudcatalog';
import timedFetch from 'middleware/timedFetch';
import { formatProductData } from 'reducers/detail/productDetail';
import type { ProductBundleOptions } from 'types/cloudCatalog';
import { selectLaunchCalendar } from 'selectors/launchCalendar';

export function setLaunchCalendarUpcomingStyles(response: LaunchCalendarUpcomingStyles) {
  return {
    type: SET_LAUNCH_CALENDAR_UPCOMING_STYLES,
    response
  } as const;
}

export function setLaunchCalendarRecentLaunches(response: LaunchCalendarRecentLaunches) {
  return {
    type: SET_LAUNCH_CALENDAR_RECENT_LAUNCHES,
    response
  } as const;
}

export function addUpcomingStylesQuickViewData(data: UpcomingStylesQuickViewData) {
  return {
    type: ADD_UPCOMING_STYLES_QUICK_VIEW_DATA,
    data
  } as const;
}

type LaunchCalendarStatus = typeof FAILED | typeof FETCHING | typeof SUCCESS;
export function setApiStatus(apiStatus: LaunchCalendarStatus) {
  return {
    type: SET_API_STATUS,
    apiStatus
  } as const;
}

export function fetchLaunchCalendarUpcomingStyles(): ThunkAction<Promise<void>, AppState, void, AnyAction> {
  return (dispatch, getState) => {
    const state = getState();
    const { cookies } = state;
    const { upcomingStyles } = selectLaunchCalendar(state) || {};

    if (upcomingStyles && Object.keys(upcomingStyles).length !== 0) {
      return;
    }

    const mafiaConfig = selectMafiaConfig(state);

    return getLaunchCalendarUpcomingStyles(mafiaConfig, cookies)
      .then(fetchErrorMiddlewareMaybeJson)
      .then((response: LaunchCalendarUpcomingStyles) => {
        if (response) {
          dispatch(setApiStatus(FETCHING));
          dispatch(setLaunchCalendarUpcomingStyles(response));
          dispatch(setApiStatus(SUCCESS));
        }
      })
      .catch((e: Error) => {
        trackError('ERROR', 'Failed to fetch launch calendar upcoming styles.', e);
        dispatch(setError(err.GENERIC, e));
        dispatch(setApiStatus(FAILED));
      });
  };
}

export function fetchLaunchCalendarRecentLaunches(): ThunkAction<Promise<void>, AppState, void, AnyAction> {
  return (dispatch, getState) => {
    const state = getState();
    const { cookies } = state;
    const { recentLaunches } = selectLaunchCalendar(state);

    if (recentLaunches && Object.keys(recentLaunches).length !== 0) {
      return;
    }

    const mafiaConfig = selectMafiaConfig(state);

    return getLaunchCalendarRecentLaunches(mafiaConfig, cookies)
      .then(fetchErrorMiddlewareMaybeJson)
      .then((response: LaunchCalendarRecentLaunches) => {
        if (response) {
          dispatch(setLaunchCalendarRecentLaunches(response));
        }
      })
      .catch((e: Error) => {
        trackError('ERROR', 'Failed to fetch launch calendar recent launches.', e);
        dispatch(setError(err.GENERIC, e));
      });
  };
}

export function subscribeToUpcomingStylesNotification(emailAddress: string, styleId: number): ThunkAction<Promise<void>, AppState, void, AnyAction> {
  return (dispatch, getState) => {
    const state = getState();
    const { cookies } = state;
    const mafiaConfig = selectMafiaConfig(state);
    return subscribeToUpcomingStylesNotificationZSub(
      mafiaConfig,
      {
        emailAddress: emailAddress,
        styleIds: [styleId]
      },
      cookies
    )
      .then(fetchErrorMiddlewareMaybeJson)
      .catch((e: Error) => {
        trackError('ERROR', 'Failed to subscribe for upcoming style notification.', e);
        dispatch(setError(err.GENERIC, e));
      });
  };
}

export function fetchProductDetails(
  fetchOpts: ProductBundleOptions,
  setErrorMessage?: (message: string) => void
): ThunkAction<void, AppState, void, AnyAction> {
  return (dispatch, getState) => {
    const {
      environmentConfig: {
        api: { cloudcatalog }
      },
      launchCalendar: { upcomingStylesQuickViewData }
    } = getState();
    const { productId = '' } = fetchOpts;

    if (productId in upcomingStylesQuickViewData) {
      return;
    }

    return productBundle(cloudcatalog, fetchOpts, timedFetch('cloudCatalogProduct'))
      .then(fetchErrorMiddleware)
      .then(response => {
        if (response.product && response.product.length >= 1 && response.product[0]) {
          const productBundleTemp = addPlaceHolderImageInResponse(response.product[0]);
          const data = {
            [productId]: { ...formatProductData({ detail: productBundleTemp }) }
          } as UpcomingStylesQuickViewData;
          dispatch(addUpcomingStylesQuickViewData(data));
          return data;
        } else {
          throw err.PRODUCT_DETAILS;
        }
      })
      .catch(() => {
        if (setErrorMessage) {
          setErrorMessage(err.PRODUCT_DETAILS);
        }
      });
  };
}

export type LaunchCalendarUpcomingProductsAction =
  | ReturnType<typeof setLaunchCalendarUpcomingStyles>
  | ReturnType<typeof setLaunchCalendarRecentLaunches>
  | ReturnType<typeof addUpcomingStylesQuickViewData>
  | ReturnType<typeof setApiStatus>;
